import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Search, Check, Sunset, Stars, Coffee, ChefHat, Film, Music, Map as MapIcon, Dices,
  Snowflake, Car, Sun, Wind, Anchor, Mountain, Leaf, Backpack, Plane, Trees, Waves,
  Droplets, Bird, Languages, Activity, Sprout, Trophy, PiggyBank, Home, Sparkles,
  Camera, UtensilsCrossed, BookOpen, Heart, Moon, Mail, Flame, Flower, Compass,
} from "lucide-react";

const ICON_MAP = {
  Sunset, Stars, Coffee, ChefHat, Film, Music, Map: MapIcon, Dices, Snowflake, Car, Sun, Wind,
  Anchor, Mountain, Leaf, Backpack, Plane, Trees, Waves, Droplets, Bird, Languages,
  Activity, Sprout, Trophy, PiggyBank, Home, Sparkles, Camera, UtensilsCrossed, BookOpen,
  Heart, Moon, Mail, Flame, Flower, Compass,
};

const PALETTE = ["#7C9082", "#C66B4A", "#D4A26A", "#2C2A29", "#A87E5D", "#6E81A0", "#A06A8A", "#8FA8A4"];

function normalize(s) {
  return (s || "").trim().toLowerCase();
}

export default function Inspiration() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("Alle");
  const [query, setQuery] = useState("");

  const [adding, setAdding] = useState(null); // inspiration item or null
  const [lists, setLists] = useState([]);
  const [entries, setEntries] = useState([]); // user's existing entries (across lists)
  const [selectedList, setSelectedList] = useState("");

  // Inline list creation
  const [listMode, setListMode] = useState("existing"); // "existing" | "new"
  const [newListTitle, setNewListTitle] = useState("");
  const [newListColor, setNewListColor] = useState(PALETTE[0]);
  const [submitting, setSubmitting] = useState(false);

  const refreshAll = async () => {
    const [i, c, l, e] = await Promise.all([
      api.get("/inspiration"),
      api.get("/inspiration/categories"),
      api.get("/lists"),
      api.get("/entries"),
    ]);
    setItems(i.data);
    setCategories(c.data);
    setLists(l.data);
    setEntries(e.data);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  // Build a map { normalized_title -> list_id } so we can show "already in <list>"
  const adoptedMap = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const key = normalize(e.title);
      if (!m.has(key)) m.set(key, e.list_id);
    }
    return m;
  }, [entries]);

  const listById = useMemo(() => {
    const m = new Map();
    for (const l of lists) m.set(l.id, l);
    return m;
  }, [lists]);

  const filtered = useMemo(() => {
    let out = items;
    if (active !== "Alle") out = out.filter((i) => i.category === active);
    const q = normalize(query);
    if (q) {
      out = out.filter(
        (i) => normalize(i.title).includes(q) || normalize(i.description).includes(q),
      );
    }
    return out;
  }, [items, active, query]);

  const openAdopt = (item) => {
    setAdding(item);
    setSelectedList("");
    setNewListTitle(item.category);
    setNewListColor(PALETTE[0]);
    setListMode(lists.length === 0 ? "new" : "existing");
  };

  const closeAdopt = () => {
    if (submitting) return;
    setAdding(null);
    setSelectedList("");
    setNewListTitle("");
    setListMode("existing");
  };

  const handleAdd = async () => {
    if (!adding) return;
    setSubmitting(true);
    try {
      let targetListId = selectedList;
      // Create new list inline if needed
      if (listMode === "new") {
        const title = newListTitle.trim() || adding.category;
        const { data: created } = await api.post("/lists", {
          title,
          description: "",
          color: newListColor,
          icon: "Sparkles",
          privacy: "couple",
        });
        targetListId = created.id;
        setLists((prev) => [created, ...prev]);
      }
      if (!targetListId) {
        toast.error("Bitte wähle eine Liste oder erstelle eine neue.");
        setSubmitting(false);
        return;
      }
      const { data: newEntry } = await api.post("/entries", {
        list_id: targetListId,
        title: adding.title,
        description: adding.description,
        status: "idea",
        priority: "medium",
        tags: [adding.category.toLowerCase()],
        wish_owner: "both",
      });
      setEntries((prev) => [newEntry, ...prev]);
      const targetList = listById.get(targetListId) || { title: newListTitle.trim() || adding.category };
      toast.success(`Zu „${targetList.title}" hinzugefügt 💛`);
      closeAdopt();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <header className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-3">Inspiration</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
          Ideen, die ihr <span className="italic text-sage">lieben</span> werdet.
        </h1>
        <p className="text-text_secondary mt-4 max-w-xl">
          Stöbert durch handverlesene Vorschläge — von Date-Ideen bis hin zu großen Reisen — und fügt sie mit einem Klick zu eurer Liste hinzu.
        </p>
      </header>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-text_secondary pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche Ideen, Reisen, Date-Nights …"
          className="pl-11 pr-4 py-6 rounded-full bg-white border-border"
          data-testid="inspiration-search"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {["Alle", ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            data-testid={`category-${c}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors ${
              active === c
                ? "bg-ink text-alabaster"
                : "bg-transparent text-text_secondary border border-border hover:border-ink hover:text-ink"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-3xl p-12 text-center" data-testid="inspiration-empty">
          <p className="text-text_secondary">Keine Ideen passend zu „{query}". Versuche einen anderen Suchbegriff.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="inspiration-grid">
          {filtered.map((item) => {
            const Icon = ICON_MAP[item.icon] || Sparkles;
            const adoptedListId = adoptedMap.get(normalize(item.title));
            const adoptedList = adoptedListId ? listById.get(adoptedListId) : null;
            const adopted = !!adoptedListId;
            return (
              <div
                key={item.id}
                className={`bg-white border rounded-2xl p-6 flex flex-col gap-3 transition-colors ${
                  adopted ? "border-sage/40" : "border-border hover:border-ink/30"
                }`}
                data-testid={`inspiration-${item.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${adopted ? "bg-sage/15" : "bg-bone"}`}>
                    <Icon size={18} strokeWidth={1.6} className={adopted ? "text-sage" : "text-ink"} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-text_secondary">{item.category}</span>
                </div>
                <h3 className="font-serif text-xl text-ink leading-snug">{item.title}</h3>
                <p className="text-sm text-text_secondary line-clamp-3 flex-1">{item.description}</p>
                {adopted ? (
                  <div className="mt-2 inline-flex items-center gap-2 text-xs text-sage" data-testid={`inspiration-adopted-${item.id}`}>
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sage/15">
                      <Check size={12} strokeWidth={2.4} />
                    </span>
                    {adoptedList ? `Bereits in „${adoptedList.title}"` : "Bereits hinzugefügt"}
                  </div>
                ) : (
                  <Button
                    onClick={() => openAdopt(item)}
                    variant="outline"
                    size="sm"
                    className="mt-2 rounded-full border-border self-start"
                    data-testid={`inspiration-add-${item.id}`}
                  >
                    <Plus size={13} className="mr-1.5" /> Zu meiner Liste
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!adding} onOpenChange={(o) => !o && closeAdopt()}>
        <DialogContent className="bg-alabaster border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Zu welcher Liste?</DialogTitle>
            <DialogDescription>Wähle eine bestehende Liste oder erstelle eine neue für „{adding?.title}".</DialogDescription>
          </DialogHeader>

          <div className="flex gap-1 p-1 bg-bone rounded-full">
            <button
              type="button"
              onClick={() => setListMode("existing")}
              disabled={lists.length === 0}
              className={`flex-1 py-2 rounded-full text-sm transition disabled:opacity-40 disabled:cursor-not-allowed ${
                listMode === "existing" ? "bg-white text-ink shadow-sm" : "text-text_secondary"
              }`}
              data-testid="inspiration-tab-existing"
            >
              Bestehende
            </button>
            <button
              type="button"
              onClick={() => setListMode("new")}
              className={`flex-1 py-2 rounded-full text-sm transition ${
                listMode === "new" ? "bg-white text-ink shadow-sm" : "text-text_secondary"
              }`}
              data-testid="inspiration-tab-new"
            >
              Neue Liste
            </button>
          </div>

          {listMode === "existing" ? (
            lists.length === 0 ? (
              <p className="text-sm text-text_secondary">Du hast noch keine Liste. Wechsle auf „Neue Liste".</p>
            ) : (
              <Select value={selectedList} onValueChange={setSelectedList}>
                <SelectTrigger className="rounded-full border-border" data-testid="inspiration-list-select">
                  <SelectValue placeholder="Liste wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {lists.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Listen-Name</label>
                <Input
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  placeholder={adding?.category || "Neue Liste"}
                  className="mt-1 rounded-2xl bg-white border-border py-5"
                  data-testid="inspiration-new-list-title"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Farbe</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="inspiration-new-list-colors">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewListColor(c)}
                      className={`w-7 h-7 rounded-full border-2 transition ${
                        newListColor === c ? "border-ink scale-110" : "border-transparent hover:scale-105"
                      }`}
                      style={{ background: c }}
                      data-testid={`inspiration-new-list-color-${c}`}
                      aria-label={`Farbe ${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeAdopt} disabled={submitting} className="rounded-full">
              Abbrechen
            </Button>
            <Button
              onClick={handleAdd}
              disabled={
                submitting ||
                (listMode === "existing" && !selectedList) ||
                (listMode === "new" && !(newListTitle.trim() || adding?.category))
              }
              className="rounded-full bg-ink text-alabaster hover:bg-ink/90"
              data-testid="inspiration-confirm-add"
            >
              {submitting ? "…" : listMode === "new" ? "Erstellen & hinzufügen" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
